/**
 * ProposalsBanner — inline proposal bar that appears just above the composer.
 *
 * Replaces the slide-in Drawer tray. Shows proposals one at a time with
 * Accept / Reject inline so the user never has to open a separate panel.
 *
 * Layout:
 *   ┌───────────────────────────────────────────────────────┐
 *   │ ✦  "skill_name"  · Auto-detected          ← 1/3 →   │
 *   │    Short description                [Reject] [Accept] │
 *   └───────────────────────────────────────────────────────┘
 *   [ composer text input ]
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, Check, Lightbulb, Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import type { Proposal } from "../../api/proposalsApi";
import type { UseProposalsResult } from "../../chat/hooks/useProposals";

// ---------------------------------------------------------------------------
// trigger_kind → human label (same map as ProposalsTray)
// ---------------------------------------------------------------------------

const TRIGGER_LABELS: Record<string, string> = {
  explicit_memorization: "You asked",
  correction_loop: "Correction",
  proactive: "Auto-detected",
  cross_session_repetition: "Repeated pattern",
  agent_proposed: "Agent proposed",
};

function triggerLabel(trigger_kind: string): string {
  return TRIGGER_LABELS[trigger_kind] ?? trigger_kind.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProposalsBannerProps {
  proposals: Proposal[];
  loading: boolean;
  onAccept: UseProposalsResult["accept"];
  onReject: UseProposalsResult["reject"];
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProposalsBanner({
  proposals,
  loading,
  onAccept,
  onReject,
}: ProposalsBannerProps) {
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ open: boolean; isSkill: boolean }>({
    open: false,
    isSkill: false,
  });

  // Keep index in bounds when proposals shrink after accept/reject.
  const safeIndex = Math.min(index, Math.max(0, proposals.length - 1));
  const proposal = proposals[safeIndex] ?? null;

  if (loading || !proposal) return null;

  const payload = proposal.payload as Record<string, string | undefined>;
  const isSkill = proposal.kind === "skill";
  const Icon = isSkill ? Sparkles : Lightbulb;
  const title = isSkill ? (payload.name ?? "Untitled skill") : (payload.text ?? "Untitled fact");
  const subtitle = isSkill ? payload.description : payload.category;
  const total = proposals.length;

  // Defensive: proposalsApi.list() maps backend "id" → proposal_id.
  // If a stale cached bundle skipped that mapping, fall back to "id" directly
  // so accept/reject URLs are never /proposals/undefined/…
  const proposalId: string =
    proposal.proposal_id ||
    (proposal as unknown as Record<string, string>).id ||
    "";

  const reset = () => {
    setBusy(false);
    setShowReject(false);
    setReason("");
    setError(null);
  };

  const handleAccept = async () => {
    if (busy) return;
    if (!proposalId) {
      setError("Proposal ID missing — try refreshing the page.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAccept(proposalId);
      // Move index back if we were on the last item.
      setIndex((i) => Math.max(0, i - (i >= proposals.length - 1 ? 1 : 0)));
      setToast({ open: true, isSkill });
      reset();
    } catch (exc) {
      setBusy(false);
      setError(exc instanceof Error ? exc.message : "Failed to accept");
    }
  };

  const handleReject = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onReject(proposalId, reason.trim() || undefined);
      setIndex((i) => Math.max(0, i - (i >= proposals.length - 1 ? 1 : 0)));
      reset();
    } catch (exc) {
      setBusy(false);
      setError(exc instanceof Error ? exc.message : "Failed to reject");
    }
  };

  return (
    <>
      <Box
        sx={{
          borderTop: 1,
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "light" ? 0.05 : 0.08),
          px: 2,
          py: 1.15,
          display: "flex",
          flexDirection: "column",
          gap: 0.9,
          borderRadius: 4,
          boxShadow: (t) => `0 10px 28px ${alpha(t.palette.primary.main, 0.06)}`,
          // Subtle entrance animation
          animation: "banner-rise 200ms cubic-bezier(0.16, 1, 0.3, 1)",
          "@keyframes banner-rise": {
            from: { opacity: 0, transform: "translateY(6px)" },
            to:   { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {/* Row 1: icon + title + chips + navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Icon size={14} aria-hidden style={{ flexShrink: 0, opacity: 0.8 }} />

          <Typography
            variant="body2"
            sx={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {title}
          </Typography>

          <Chip
            size="small"
            label={triggerLabel(proposal.trigger_kind)}
            sx={{ fontSize: 11, height: 22, opacity: 0.85, flexShrink: 0 }}
          />

          {/* Prev / index / next — only shown when >1 proposal */}
          {total > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => { setIndex((i) => i - 1); reset(); }}
                    disabled={safeIndex === 0}
                    sx={{ p: 0.25 }}
                  >
                    <ChevronLeft size={14} />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" sx={{ opacity: 0.6, userSelect: "none" }}>
                {safeIndex + 1}/{total}
              </Typography>
              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => { setIndex((i) => i + 1); reset(); }}
                    disabled={safeIndex === total - 1}
                    sx={{ p: 0.25 }}
                  >
                    <ChevronRight size={14} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Row 2: subtitle + actions (or reject input) */}
        {showReject ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Escape") { setShowReject(false); setReason(""); } }}
            />
            <Button size="small" color="inherit" onClick={() => { setShowReject(false); setReason(""); }} disabled={busy}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={busy}
              sx={{ whiteSpace: "nowrap" }}
            >
              {busy ? <CircularProgress size={13} /> : "Confirm reject"}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {subtitle}
              </Typography>
            )}
            {!subtitle && <Box sx={{ flex: 1 }} />}

            {error && (
              <Typography variant="caption" color="error.main" sx={{ flexShrink: 0 }}>
                {error}
              </Typography>
            )}

            <Button
              size="small"
              color="inherit"
              startIcon={<X size={13} />}
              onClick={() => setShowReject(true)}
              disabled={busy}
              sx={{ flexShrink: 0 }}
            >
              Reject
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={busy ? <CircularProgress size={13} /> : <Check size={13} />}
              onClick={handleAccept}
              disabled={busy}
              sx={{ flexShrink: 0 }}
            >
              Accept
            </Button>
          </Box>
        )}
      </Box>

      {/* Success toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={
          toast.isSkill ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Check size={14} />
              Skill saved.{" "}
              <Link
                to="/workspace/skills"
                style={{ color: "inherit", fontWeight: 600 }}
                onClick={() => setToast((t) => ({ ...t, open: false }))}
              >
                View Skills →
              </Link>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Check size={14} />
              Fact remembered.
            </Box>
          )
        }
      />
    </>
  );
}
